import {
  AviaLegTypes,
  GroundLegTypes,
  RouteLegType,
  RouteLegTypeEnum,
  TmdxAffiliateGroundLeg,
  TxCenterProviderGroundLeg,
} from '@/api/apis/route';
import { RouteFilterType, RouteSegmentType } from '@/types/route';
import { CaseResourceType } from '@/api/apis/case';
import { getSegmentMapKey, isAviaLegType } from '@/utils/route';

export const getFilteredRouteLegs = <T extends RouteLegType>(legs: T[], filterType: RouteFilterType): T[] => {
  if (filterType === RouteFilterType.Full) return legs;

  return legs.filter((leg) => {
    return leg.resources.some((r) => {
      const isCS = filterType === RouteFilterType.CS;
      const isSurgeon = filterType === RouteFilterType.Surgeon;

      return (
        (isCS && r.resourceType === CaseResourceType.ClinicalSpecialist) ||
        (isSurgeon && r.resourceType === CaseResourceType.Surgeon)
      );
    });
  });
};

type LegProvidersListType = {
  hasTmdxAvia: boolean;
  hasSupplementalLiftAvia: boolean;
  isTxCenterAvia: boolean;
  hasTmdxAffiliateGround: boolean;
  hasSelfServicedGround: boolean;
  hasTxCenterGround: boolean;
  tmdxAffiliateLegs: TmdxAffiliateGroundLeg[];
};

export const getLegProviders = (legs: (AviaLegTypes | GroundLegTypes)[]): LegProvidersListType => {
  const aviaLegs = legs.filter((l): l is AviaLegTypes => isAviaLegType(l));
  const tmdxAffiliateLegs = legs.filter(
    (l): l is TmdxAffiliateGroundLeg => l.legType === RouteLegTypeEnum.TmdxAffiliateGroundLeg,
  );
  const tmdxDriveAffiliateLegs = legs.filter(
    (l): l is TmdxAffiliateGroundLeg => l.legType === RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
  );
  const selfServicedLegs = legs.filter((l) => l.legType === RouteLegTypeEnum.SelfServicedGroundLeg);
  const txGroundLegs = legs.filter(
    (l): l is TxCenterProviderGroundLeg => l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg,
  );

  return {
    // Avia Legs
    hasTmdxAvia: aviaLegs.some((l) => l.legType === RouteLegTypeEnum.TmdxJetAviaLeg),
    hasSupplementalLiftAvia: aviaLegs.some((l) => l.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg),
    isTxCenterAvia: aviaLegs.every((l) =>
      [RouteLegTypeEnum.CommercialFlightAviaLeg, RouteLegTypeEnum.TxCenterProviderAviaLeg].includes(l.legType),
    ),
    // Ground Legs
    hasTmdxAffiliateGround: !!tmdxAffiliateLegs.length || !!tmdxDriveAffiliateLegs.length,
    hasSelfServicedGround: !!selfServicedLegs.length,
    hasTxCenterGround: !!txGroundLegs.length,
    tmdxAffiliateLegs,
  };
};

export const getPreviousLeg = (
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
  segment: RouteSegmentType,
  prevLegId: string,
) => {
  const segmentLegs = legs.get(getSegmentMapKey(segment)) || [];

  return segmentLegs.find((leg) => prevLegId === leg.id) || null;
};
